import { ReactElement, Suspense, useMemo } from 'react'
import { DoubleSide, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import useLabelScaleFromPlanSettings from '@editor/hooks/useLabelScaleFromPlanSettings'
import usePlanTableLayoutMetrics from '@editor/hooks/usePlanTableLayoutMetrics'
import useSceneLineMarker from '@editor/hooks/useSceneLineMarker'
import { ThreeEvent } from '@react-three/fiber'
import { computePlanTableBounds } from 'src/domain/planTables/positionAnchor'
import { computePdfTableLayout } from 'src/domain/planTables/tableLayout'
import type { PlanTable } from 'src/domain/planTables/types'
import SceneLineMarker from '../SceneLineMarker'
import Text3DThree from '../Text3DThree'
import PlanTableContent from './PlanTableContent'

const TABLE_HIT_Z_OFFSET = 0.03
const TABLE_CONTENT_Z_OFFSET = 0.01

interface Props {
  /** The table with the content the plan draws (see `usePlanViewPlanTables`). */
  planTable: PlanTable
  selected: boolean
  /**
   * Draw the table as the exported plan does (Planansicht). Otherwise it is drawn as a generic
   * outline with its name.
   */
  showContent?: boolean
  /**
   * True while this table is being dragged. The frame is then drawn inline
   * instead of through `useSceneLineMarker`, which otherwise re-registers a
   * global scene marker on every drag frame (add + remove) and makes the drag
   * lag. See the plan table controller.
   */
  isDragging?: boolean
  onClick: () => void
  /** Fires when a scene click misses this table (used to deselect the active table). */
  onPointerMissed?: (event: MouseEvent) => void
}

const PlanTablePreview = ({
  planTable,
  selected,
  showContent = false,
  isDragging = false,
  onClick,
  onPointerMissed,
}: Props): ReactElement => {
  const theme = useTheme()
  const labelScale = useLabelScaleFromPlanSettings()
  const { fontSize, ptToWorld } = usePlanTableLayoutMetrics()

  const pdfLayout = useMemo(
    () => computePdfTableLayout(planTable.table, fontSize),
    [planTable.table, fontSize],
  )
  const totalWidth = pdfLayout.totalWidth * ptToWorld
  const totalHeight = pdfLayout.totalHeight * ptToWorld

  const bounds = useMemo(
    () =>
      computePlanTableBounds(
        planTable.position,
        { width: totalWidth, height: totalHeight },
        planTable.positionAnchor,
      ),
    [planTable.position, planTable.positionAnchor, totalHeight, totalWidth],
  )

  const z = planTable.position.z
  const { xMin: x0, yMin: y0, xMax: x1, yMax: y1 } = bounds

  const frameLines = useMemo(
    () => [
      [
        { x: x0, y: y0, z },
        { x: x1, y: y0, z },
      ],
      [
        { x: x1, y: y0, z },
        { x: x1, y: y1, z },
      ],
      [
        { x: x1, y: y1, z },
        { x: x0, y: y1, z },
      ],
      [
        { x: x0, y: y1, z },
        { x: x0, y: y0, z },
      ],
    ],
    [x0, x1, y0, y1, z],
  )

  const borderColor = selected
    ? (theme.palette.primary.main as string)
    : (theme.scenePalette.planArea.frame as string)

  useSceneLineMarker({
    lines: frameLines,
    // While dragging, the frame is rendered inline (below) instead, to avoid the
    // per-frame global-store churn of the marker hook. The plan's own table has no frame, so
    // with its content shown it is only outlined while selected.
    enabled: planTable.isVisible && !isDragging && (!showContent || selected),
    color: borderColor,
    lineWidth: selected ? 3 : 2,
    renderOrder: 900,
  })

  const contentOrigin = useMemo(() => new Vector3(x0, y0, z + TABLE_CONTENT_Z_OFFSET), [x0, y0, z])

  const namePosition = useMemo(
    () => new Vector3((x0 + x1) / 2, (y0 + y1) / 2, z + 0.01),
    [x0, x1, y0, y1, z],
  )

  const hitTargetCenter = useMemo(
    () => new Vector3((x0 + x1) / 2, (y0 + y1) / 2, z + TABLE_HIT_Z_OFFSET),
    [x0, x1, y0, y1, z],
  )

  const handleSelect = (event: ThreeEvent<MouseEvent>): void => {
    event.stopPropagation()
    onClick()
  }

  if (!planTable.isVisible) return <></>

  return (
    <group renderOrder={1000} onPointerMissed={onPointerMissed}>
      <mesh
        position={hitTargetCenter}
        renderOrder={1000}
        onClick={handleSelect}
        onPointerOver={event => {
          event.stopPropagation()
          document.body.style.cursor = 'pointer'
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'auto'
        }}
      >
        <planeGeometry args={[Math.max(totalWidth, 0.05), Math.max(totalHeight, 0.05)]} />
        <meshBasicMaterial transparent opacity={0} depthTest={false} side={DoubleSide} />
      </mesh>

      {isDragging && (
        <SceneLineMarker
          lines={frameLines}
          color={borderColor}
          lineWidth={selected ? 3 : 2}
          renderOrder={900}
          disableDepthTest
        />
      )}

      {showContent ? (
        <Suspense fallback={null}>
          <PlanTableContent layout={pdfLayout} ptToWorld={ptToWorld} origin={contentOrigin} />
        </Suspense>
      ) : (
        <Text3DThree
          textLines={['Tabelle', planTable.name]}
          color={theme.palette.text.primary}
          backgroundColor={theme.palette.background.paper}
          borderColor={borderColor}
          borderWidth={0.001}
          position={namePosition}
          scale={labelScale}
          anchorLocation="center"
          onClick={handleSelect}
        />
      )}
    </group>
  )
}

export default PlanTablePreview
